import { CommonModule } from '@angular/common';
import { Component, computed, input, Input } from '@angular/core';
import { EChartsOption, SeriesOption } from 'echarts';
import { NgxEchartsDirective } from 'ngx-echarts';

export interface LineChartData {
  category: string;
  [key: string]: string | number | null;
}

export interface DynamicLineSeries {
  valueField: string;
  argumentField: string;
  name: string;
  color: string;
}

type SeriesConfig = {
  color: string[];
  seriesOptions: SeriesOption[];
};
@Component({
  selector: 'app-line-chart-card',
  standalone: true,
  imports: [CommonModule, NgxEchartsDirective],
  templateUrl: './line-chart-card.component.html',
  styleUrl: './line-chart-card.component.scss',
})
export class LineChartCardComponent {
  @Input() title = '';
  data = input<LineChartData[]>([]);
  series = input<DynamicLineSeries[]>([]);
  visualRange = input<number[]>([]);
  xAxisType = input<'category' | 'time'>('category');
  zoomable = input(false);

  chartOptions = computed(() => {
    const data = this.data();
    const series = this.series();
    const visualRange = this.visualRange();

    const xAxisType = this.xAxisType();
    const zoomable = this.zoomable();

    if (data.length && series.length) {
      const { color, seriesOptions } = series.reduce(
        (acc, cur): SeriesConfig => ({
          color: [...acc.color, cur.color],
          seriesOptions: [
            ...acc.seriesOptions,
            {
              type: 'line',
              connectNulls: false,
              emphasis: {
                focus: 'self',
              },
              encode: {
                x: cur.argumentField,
                y: cur.valueField,
              },
              name: cur.name,
            },
          ],
        }),
        {
          color: [],
          seriesOptions: [],
        } as SeriesConfig,
      );

      const options: EChartsOption = {
        color,
        title: {
          show: false,
          // text: this.title,
          // textStyle: {
          //   fontFamily: 'aktiv-grotesk',
          //   fontWeight: 'normal',
          //   fontSize: '16px',
          // },
          // left: 0,
        },
        dataset: {
          dimensions: [
            series[0].argumentField,
            ...series.map((s) => s.valueField),
          ],
          source: data,
        },
        grid: {
          left: 10,
          right: 10,
          bottom: zoomable ? 60 : 10,
          containLabel: true,
        },
        series: seriesOptions,
        xAxis: { type: xAxisType },
        yAxis: {
          type: 'value',
          ...(visualRange.length
            ? { min: visualRange[0], max: visualRange[1] }
            : {}),
        },
        tooltip: {
          show: true,
          trigger: 'axis',
        },
        ...(zoomable
          ? {
              dataZoom: [
                {
                  type: 'slider',
                  backgroundColor: '#f3faf5',
                  fillerColor: 'rgba(5, 153, 49, 0.2)',
                  borderColor: 'rgba(5, 153, 49, 0.3)',
                  handleStyle: { color: '#059931', borderColor: '#047a28' },
                  moveHandleStyle: { color: '#059931' },
                  dataBackground: {
                    lineStyle: { color: '#94d3a6' },
                    areaStyle: { color: 'rgba(5, 153, 49, 0.1)' },
                  },
                  selectedDataBackground: {
                    lineStyle: { color: '#059931' },
                    areaStyle: { color: 'rgba(5, 153, 49, 0.3)' },
                  },
                  emphasis: {
                    handleLabel: { show: false },
                    handleStyle: { color: '#047a28', borderColor: '#035f1f' },
                    moveHandleStyle: { color: '#047a28' },
                  },
                },
                {
                  type: 'inside',
                  zoomOnMouseWheel: true,
                  moveOnMouseWheel: false,
                  moveOnMouseMove: true,
                },
              ],
            }
          : {}),
      };

      return options;
    }

    return null;
  });
}
